<template>
  <div class="tab-container mt8" :style="{
    'min-height': props.tabContainerMinHeight,
    'max-height': props.tabContainerMaxHeight,
  }">
    <ul class="tab-bar">
      <li
        class="tab-item"
        v-for="tabLabel of tabLabels"
        :key="tabLabel.dataName"
        :class="{active: state.activeTab === tabLabel.dataName}"
        @click="onTabClick(tabLabel.dataName)"
      >
        {{ tabLabel.label }}
      </li>
    </ul>
    <div
      class="tab-content"
      :style="{'max-height': props.tabContentMaxHeight}"
    >
      <div
        class="tab-pane"
        :class="{active: state.activeTab === tabEntry.dataName}"
        v-for="tabEntry of tabData"
        :key="tabEntry.dataName"
      >
        <template v-if="!tabEntry.resources.ready">
          <div class="tab-pane-element">
            <div class="ta-center">
              <i class="fa-2x fas fa-spinner fa-spin"></i>
            </div>
          </div>
        </template>
        <template v-else>
          <template v-if="tabEntry.dataName === 'comment'">
            <div
              class="tab-pane-element elements"
              @click="onRecentCommentsClick(e)"
              v-for="e in commentElements(tabEntry)"
              :key="e.id"
            >
              <div class="elements-meta mb4">
                <div class="title">
                  <div
                    v-if="!!e.commentTypeColorCode"
                    class="ib vm color-box"
                    :class="[`color-${e.commentTypeColorCode}`]"
                  ></div>
                  <span v-if="!!e.commentTypeDisp" class="ib vm comment-type">【{{e.commentTypeDisp}}】</span>
                  <span v-if="!!e.userDisplayName" class="ib vm username-disp">{{e.userDisplayName}}</span>
                </div>
                <div v-if="!!e.ts" class="ts">{{fuzzyTimeDisp(e.ts, new Date())}}</div>
              </div>
              <div v-if="!!e.kpDisp" class="mb2">場所: {{e.kpDisp}}</div>
              <div class="elements-content" v-html="e.contentDisp"></div>
            </div>
          </template>
          <template v-else>
            <div
              class="tab-pane-element elements"
              @click="onTabElementClick(e, tabEntry.dataName)"
              v-for="e in tabPaneElements(tabEntry)"
              :key="e.id"
            >
              <div class="elements-meta mb4">
                <div class="title">
                  <span v-if="!!e.titleDisp" class="ib vm title-disp ">{{ e.titleDisp }}</span>
                </div>
                <div v-if="!!e.ts" class="ts">{{fuzzyTimeDisp(e.ts, new Date())}}</div>
              </div>
              <div v-if="!!e.kpDisp" class="mb2">場所: {{e.kpDisp}}</div>
              <div class="elements-content" v-html="e.contentDisp"></div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  reactive,
  watch,
} from 'vue';
import type { MapElemInfo, Tab, TabResources } from 'src/models';
import type { GIComment, GIResourceWithID } from 'src/models/geoItem';
import type { RecentCommentElement } from 'src/models/comment';
import { fuzzyTimeDisp } from 'src/lib/dateHelper';

interface TabLable {
  dataName: string;
  label: string;
}

interface TabData {
  dataName: string;
  resources: TabResources;
}

// comment以外のタブ要素でテンプレートが参照する表示用フィールド。
// 実体はレイヤーごとに異なるGIリソースで、フィールドを持たない場合はv-ifで非表示になる。
interface TabPaneElement {
  id: number | string;
  ts?: Date;
  titleDisp?: string;
  kpDisp?: string;
  contentDisp?: string;
}

interface TabPaneState {
  activeTab: string;
}

interface Props {
  tabs: Tab[];
  defaultActiveTab: string;
  tabContainerMinHeight: string;
  tabContainerMaxHeight: string;
  tabContentMaxHeight: string;
}

const props = defineProps<Props>();

const emit = defineEmits(['click']);

const state = reactive<TabPaneState>({
  activeTab: '',
});

const tabLabels = computed<TabLable[]>(() => {
  return props.tabs.map(e => {
    return { dataName: e.dataName, label: e.label };
  });
});
const tabData = computed<TabData[]>(() => {
  return props.tabs.map(e => {
    return { dataName: e.dataName, resources: e.resources };
  });
});

onMounted(() => {
  state.activeTab = props.defaultActiveTab;
});

// watch:
watch(() => props.tabs, () => {
  // tabsの中にactiveTabが含まれない時、選択を初期化する
  if (!props.tabs.map(e => e.dataName).includes(state.activeTab)) {
    state.activeTab = props.defaultActiveTab;
  }
});

// methods
const onTabClick = (dataName: string) => {
  state.activeTab = dataName;
};
// commentタブの要素はuseRecentComment側でGIComment+kpDispとして構築されるため、その型へ絞り込む
const commentElements = (tab: TabData): RecentCommentElement[] => {
  return tab.resources.elements as RecentCommentElement[];
};
// comment以外のタブ要素は実体がレイヤーごとに異なるため、テンプレートで参照する表示用フィールドの型へ絞り込む
const tabPaneElements = (tab: TabData): TabPaneElement[] => {
  return tab.resources.elements as TabPaneElement[];
};
const onRecentCommentsClick = (comment: GIComment) => {
  const params: MapElemInfo<GIComment> = {
    data: comment,
    dataName: 'comment',
  };
  emit('click', params);
};
const onTabElementClick = (element: TabPaneElement, dataName: string) => {
  const params: MapElemInfo<GIResourceWithID> = {
    // タブ要素の実体はGIResourceWithID。表示用に絞り込んだ型から実体の型へ戻す
    data: element as GIResourceWithID,
    dataName,
  };
  emit('click', params);
};
</script>

<style lang="scss" scoped>
.tab-container {
  .tab-bar {
    .tab-item {
      min-width: 100px;
      background-color: #a4cad6;
      color: #fdfdfd;
    }
  }

  .tab-content {
    .elements {
      cursor: pointer;
      .elements-meta {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        .color-box {
          width: 8px;
          height: 8px;
          margin: -1px -2px 0 1px;
          border-radius: 2px;
          &.color-01 { background-color: #418ab3; }
          &.color-02 { background-color: #fec306; }
          &.color-03 { background-color: #7ab727; }
          &.color-04 { background-color: #838383; }
          &.color-05 { background-color: #df5327; }
          &.color-06 { background-color: #9f66b6; }
          &.color-07 { background-color: #9ccdd4; }
          &.color-08 { background-color: #008c5e; }
          &.color-09 { background-color: #ebe1b5; }
          &.color-10 { background-color: #3b5194; }
          &.color-11 { background-color: #fcb699; }
          &.color-12 { background-color: #e948a5; }
        }
        .comment-type {
          font-weight: bold;
        }
        .title-disp {
          font-weight: bold;
        }
      }
      .elements-content {
        font-size: 0.8rem;
      }
    }
  }
}
</style>
